import React, { useState, useEffect } from 'react';
import { getProfile } from '../services/api';
import ContactModal from './ContactModal';

const Hero = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await getProfile();
        setProfile(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-700 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="text-center">
          <p className="text-slate-600">Unable to load profile</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Subtle gradient orbs - muted tones */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-br from-slate-200 to-slate-300 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-amber-100 to-orange-200 rounded-full blur-3xl opacity-20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-slate-200 rounded-full opacity-30" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-slate-100 rounded-full opacity-20" />
        
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            {/* Avatar */}
            <div className="mb-12 flex justify-center">
              <div className="relative group">
                <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-slate-800 transition-transform duration-300 group-hover:scale-105 shadow-xl">
                  {profile.avatar ? (
                    <img 
                      src={profile.avatar} 
                      alt={profile.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                      <span className="text-4xl font-bold text-slate-400">
                        {profile.name?.charAt(0) || 'N'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center text-amber-400 text-xl shadow-lg">
                  ⚡
                </div>
              </div>
            </div>

            {/* Name */}
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 text-slate-800">
              {profile.name}
            </h1>

            {/* Title - Three-color palette matching timeline tracks */}
            <div className="mb-8 max-w-4xl mx-auto px-4">
              <p className="text-base sm:text-lg md:text-xl font-medium leading-relaxed flex flex-wrap justify-center gap-x-2 gap-y-1">
                <span className="text-slate-700 font-semibold">Tech Enthusiast</span>
                <span className="text-slate-300">|</span>
                <span className="text-amber-600 font-semibold">AI Architect</span>
                <span className="text-slate-300">|</span>
                <span className="text-rose-500 font-semibold">Data & Cloud Engineer</span>
                <span className="text-slate-300">|</span>
                <span className="text-slate-700 font-semibold">Web3 Advocate</span>
                <span className="text-slate-300">|</span>
                <span className="text-amber-600 font-semibold">Venture Investor</span>
              </p>
            </div>

            {/* Bio */}
            <p className="text-xl md:text-2xl text-slate-600 max-w-3xl mx-auto mb-12 leading-relaxed font-light italic">
              "{profile.bio}"
            </p>

            {/* CTA - Executive style buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={() => document.getElementById('timeline')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-slate-800 text-white text-lg font-semibold tracking-wide hover:bg-slate-700 transition-all duration-300 group shadow-lg hover:shadow-xl rounded-xl"
              >
                Explore My Journey
                <span className="inline-block ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
              </button>
              <button 
                onClick={() => setShowContactModal(true)}
                className="px-8 py-4 border-2 border-slate-800 text-slate-800 text-lg font-semibold tracking-wide hover:bg-slate-800 hover:text-white transition-all duration-300 rounded-xl shadow-lg hover:shadow-xl"
              >
                Get in Touch
              </button>
            </div>

            {/* Scroll indicator */}
            <div className="mt-24 animate-bounce">
              <div className="w-6 h-10 border-2 border-slate-400 rounded-full flex justify-center pt-2 mx-auto">
                <div className="w-1 h-2 bg-slate-400 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />
    </>
  );
};

export default Hero;
